import type { ParsedDxfPreview } from "./dxfParse";

/**
 * Snap candidates extracted from a parsed preview, used by the
 * measurement tool to lock clicks to interesting geometry rather than
 * the user's freehand cursor.
 *
 * Two snap kinds today:
 *   - `hole-center`: centroid of each hole/inner loop (rendered as
 *     "STŘED OTVORU" in the sidebar)
 *   - `corner`: bbox corners of the outer loop (helps measuring full
 *     part dimensions)
 *
 * All coordinates live in SVG user-space (the 800×600 viewBox).
 */

export type SnapKind = "hole-center" | "corner";

export interface SnapPoint {
  kind: SnapKind;
  x: number;
  y: number;
  /** Optional id for downstream consumers (loop or component id). */
  id?: string;
}

export interface SnapHit {
  point: SnapPoint;
  /** Distance from cursor in SVG user-space units. */
  distance: number;
}

export function snapCandidates(parsed: ParsedDxfPreview | null): SnapPoint[] {
  if (!parsed) return [];
  const out: SnapPoint[] = [];
  parsed.loops.forEach((shape, id) => {
    if (shape.role === "hole" || shape.role === "inner") {
      out.push({ kind: "hole-center", x: shape.centroid.x, y: shape.centroid.y, id });
    }
    if (shape.role === "outer" || shape.role === "main") {
      const b = shape.bbox;
      out.push({ kind: "corner", x: b.x, y: b.y, id });
      out.push({ kind: "corner", x: b.x + b.w, y: b.y, id });
      out.push({ kind: "corner", x: b.x, y: b.y + b.h, id });
      out.push({ kind: "corner", x: b.x + b.w, y: b.y + b.h, id });
    }
  });
  return out;
}

/**
 * Returns the nearest candidate within `maxDistance` (in SVG user-space)
 * of the cursor, or `null` for no snap.
 *
 * Default 24 is generous on purpose — at the analyzer's 800×600 viewBox
 * with typical 24-px padding around the geometry, that's ~3% of width,
 * which feels right when zoomed out but still discriminates between
 * neighbouring snap targets when zoomed in.
 */
export function findNearestSnap(
  cursor: { x: number; y: number },
  candidates: SnapPoint[],
  maxDistance = 24,
): SnapHit | null {
  let best: SnapHit | null = null;
  for (const c of candidates) {
    const dx = c.x - cursor.x;
    const dy = c.y - cursor.y;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d <= maxDistance && (best === null || d < best.distance)) {
      best = { point: c, distance: d };
    }
  }
  return best;
}
